import type { ReactNode } from 'react';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  SignalSlashIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from './Button';

export type DegradedStateSeverity = 'error' | 'degraded';
export type DegradedStateVariant = 'page' | 'compact';

interface DegradedStateProps {
  title: string;
  description: string;
  severity?: DegradedStateSeverity;
  variant?: DegradedStateVariant;
  retryLabel?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
  recoveryHint?: string;
  children?: ReactNode;
  className?: string;
  stateKind?:
    'unavailable' | 'degraded' | 'failure' | 'partial-success' | 'partial' | 'stale' | 'retrying';
}

const severityStyles = {
  error: {
    eyebrow: 'Data unavailable',
    icon: SignalSlashIcon,
    shell:
      'border-rose-500/25 bg-gradient-to-br from-rose-500/[0.08] via-[rgb(var(--color-bg-secondary))] to-[rgb(var(--color-bg-secondary))]',
    iconShell: 'border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-300',
    eyebrowClass: 'text-rose-700 dark:text-rose-300',
    glow: 'bg-rose-500/10',
  },
  degraded: {
    eyebrow: 'Partial results',
    icon: ExclamationTriangleIcon,
    shell:
      'border-amber-500/25 bg-gradient-to-br from-amber-500/[0.08] via-[rgb(var(--color-bg-secondary))] to-[rgb(var(--color-bg-secondary))]',
    iconShell: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300',
    eyebrowClass: 'text-amber-700 dark:text-amber-300',
    glow: 'bg-amber-500/10',
  },
} as const;

/**
 * A truthful, recoverable state for unavailable or partially available data.
 * This is intentionally distinct from EmptyState: an empty result is valid
 * data, while this component tells the operator that Hyperion could not
 * verify the result.
 */
export function DegradedState({
  title,
  description,
  severity = 'error',
  variant = 'page',
  retryLabel = 'Try again',
  onRetry,
  isRetrying = false,
  recoveryHint,
  children,
  className,
  stateKind,
}: DegradedStateProps) {
  const reduceMotion = useReducedMotion();
  const styles = severityStyles[severity];
  const Icon = styles.icon;
  const isCompact = variant === 'compact';
  const eyebrow =
    stateKind === 'stale'
      ? 'Stale results'
      : stateKind === 'retrying'
        ? 'Checking again'
        : stateKind === 'failure'
          ? 'Operation failed'
          : stateKind === 'partial-success'
            ? 'Partially completed'
            : styles.eyebrow;

  return (
    <motion.section
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
      role={severity === 'error' ? 'alert' : 'status'}
      data-state-kind={stateKind ?? (severity === 'degraded' ? 'degraded' : 'failure')}
      data-intentional-overflow="decorative-glow"
      aria-live={severity === 'error' ? 'assertive' : 'polite'}
      aria-busy={isRetrying}
      className={clsx(
        'relative isolate overflow-hidden border',
        styles.shell,
        isCompact
          ? 'rounded-xl px-4 py-3.5'
          : 'flex min-h-64 items-center rounded-2xl px-6 py-10 sm:px-10',
        className
      )}
    >
      <div
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute -right-16 -top-20 -z-10 h-52 w-52 rounded-full blur-3xl',
          styles.glow
        )}
      />

      <div
        className={clsx(
          'relative flex w-full',
          isCompact ? 'items-start gap-3.5' : 'items-start gap-5'
        )}
      >
        <div
          className={clsx(
            'flex shrink-0 items-center justify-center border shadow-sm',
            styles.iconShell,
            isCompact ? 'h-9 w-9 rounded-lg' : 'h-12 w-12 rounded-xl'
          )}
        >
          <Icon className={isCompact ? 'h-4.5 w-4.5' : 'h-6 w-6'} />
        </div>

        <div className="min-w-0 flex-1">
          <p
            className={clsx(
              'font-semibold uppercase tracking-[0.16em]',
              isCompact ? 'text-[9px]' : 'text-[10px]',
              styles.eyebrowClass
            )}
          >
            {eyebrow}
          </p>
          <h2
            className={clsx(
              'text-[rgb(var(--color-text-primary))]',
              isCompact ? 'mt-0.5 text-sm font-semibold' : 'mt-1 text-lg font-semibold sm:text-xl'
            )}
          >
            {title}
          </h2>
          <p
            className={clsx(
              'max-w-2xl text-[rgb(var(--color-text-secondary))]',
              isCompact ? 'mt-1 text-xs' : 'mt-2 text-sm leading-6'
            )}
          >
            {description}
          </p>

          {recoveryHint ? (
            <p
              className={clsx(
                'text-[rgb(var(--color-text-tertiary))]',
                isCompact ? 'mt-1.5 text-[11px]' : 'mt-3 text-xs'
              )}
            >
              {recoveryHint}
            </p>
          ) : null}

          {children}

          {onRetry ? (
            <div className={isCompact ? 'mt-3' : 'mt-5'}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isRetrying}
                onClick={() => void onRetry()}
                className="gap-2 bg-[rgb(var(--color-bg-secondary)/0.82)]"
              >
                <ArrowPathIcon
                  aria-hidden="true"
                  className={clsx('h-3.5 w-3.5', isRetrying && 'motion-loading-spin')}
                />
                {isRetrying ? 'Checking again…' : retryLabel}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </motion.section>
  );
}
